import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toQuery, useQuery } from '../Routes';
import { Main } from '../Main/Main';
import { LOOP54_DEFAULT_PAGE_SIZE, PagesLoop54 } from '../Pagination/PaginationLoop54';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { generateUUID, renderTemplate } from '../utils';
import { useApi, Res } from '../../state/api';
import { Interpolated } from '../Interpolated';
import { SearchFacet } from '../SearchFacet/SearchFacet';
import { ContentCard } from '../ContentCard/ContentCard';
import './SearchPage.css';
import { ProductBox } from '../ProductListing/ProductBox/ProductBox';
import { useConfig } from '../../config';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { MetaContainer } from '../../state/meta';
import { Loader } from '../Loader/Loader';
import { Loop54SearchRequestBody } from '../../types';
import { useSession } from '../../state/session';

export const SearchPageLoop54 = (): JSX.Element => {
	const config = useConfig();
	const t = useTranslation().search;
	const meta = MetaContainer.useContainer();
	const [isNewSearch, setIsNewSearch] = useState(true);
	const { isLoggedIn, isLoading: loginStatusIsLoading } = useSession();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	let { q, facetCategory, page } = useQuery<{ q: string; facetCategory: string; page: string }>();
	q = q?.slice(0, 100);
	let loop54SearchPayload: Loop54SearchRequestBody = {
		query: q,
		resultsOptions: {
			take: LOOP54_DEFAULT_PAGE_SIZE,
			skip: 0,
		},
		spellingSuggestionsOptions: {
			skip: 0,
			take: 1,
		},
		relatedResultsOptions: {
			take: 0,
			skip: 0,
		},
	};

	if (facetCategory) {
		const categoryId = extractCategoryId(facetCategory);
		if (categoryId) {
			loop54SearchPayload.resultsOptions.facets = [
				{
					name: 'categoryFacet',
					attributeName: 'facetCategoryId',
					type: 'distinct',
					selected: [categoryId],
				},
			];
		}
	}

	const loop54PostHeaders = {
		'Api-Version': 'V3',
		'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
	};

	const { data, error, isLoading } = useApi(
		q ? Res.searchProductsLoop54 : null,
		{ limit: LOOP54_DEFAULT_PAGE_SIZE, offset: 0, q, facetCategory },
		config.loop54Api,
		loop54SearchPayload,
		loop54PostHeaders,
		'POST'
	);

	function extractCategoryId(facetCategoryString: string): string | null {
		const match = facetCategoryString.match(/_(\d+)/);
		return match ? match[1] : null;
	}

	useEffect(() => {
		if (typeof window !== 'undefined' && window.plausible) {
			window.plausible('search', { props: { engine: meta.searchEngine } });
			if (meta.searchEngine === 'solr') window.plausible('searchSolr');
			if (meta.searchEngine === 'loop') window.plausible('searchLoop');
		}
		setIsNewSearch(true);
	}, [q, facetCategory]);

	useEffect(() => {
		if (!loginStatusIsLoading) {
			if (meta.searchEngine === 'solr' && !isLoggedIn) {
				if (typeof window !== 'undefined' && window.plausible) {
					window.plausible('searchSolrProspect');
				}
			}
			if (meta.searchEngine === 'loop' && !isLoggedIn) {
				if (typeof window !== 'undefined' && window.plausible) {
					window.plausible('searchLoopProspect');
				}
			}
		}
	}, [isLoggedIn, loginStatusIsLoading, q, facetCategory]);

	/*
	 * A constant identity: this goes to every memoised product box in the results, so a callback
	 * rebuilt when the page or the flag changes would invalidate all of them on exactly the
	 * interaction - "Visa fler" - the memo is there to make cheap. The values are read when it
	 * runs instead.
	 */
	const searchEventState = useRef({ isNewSearch, page });
	/*
	 * Written as the render commits, not during it and not after the paint: during it is what a
	 * concurrent render may throw away, and a passive effect runs late enough that a purchase made
	 * the instant a page lands would report against the page before it. The same idiom as
	 * TopBanner and AccountPageNavigation, since a layout effect alone warns on the server.
	 */
	const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
	useIsomorphicLayoutEffect(() => {
		searchEventState.current = { isNewSearch, page };
	}, [isNewSearch, page]);
	const sendSuccessfulSearchEvent = useCallback(() => {
		const { isNewSearch: unreported, page: currentPage } = searchEventState.current;
		if (unreported && typeof currentPage === 'undefined') {
			if (typeof window !== 'undefined' && window.plausible) {
				window.plausible('successfulSearch');
				setIsNewSearch(false);
			}
		}
	}, []);

	if (!q)
		return (
			<Main>
				<NoSearchQuery />
			</Main>
		);

	if (isLoading)
		return (
			<Main>
				<Loader />
			</Main>
		);
	if (error)
		return (
			<Main>
				<ErrorMessage message={error.message} />
			</Main>
		);
	if (!data || !data.results || !data.results.items)
		return (
			<Main>
				<p>{t.noResultsFound}</p>
			</Main>
		);

	const suggest = data.spellingSuggestions.count > 0 && data.results.items.length === 0;

	return (
		<Main>
			<h1 className="search-page-title">
				<QueryDescription query={q} total={data.results.count} facet={facetCategory} />
			</h1>
			{suggest ? <Suggestion suggestion={data.spellingSuggestions.items[0].query} /> : null}
			{data.results.count === 0 && <NoResults />}
			<PagesLoop54
				resource={Res.searchProductsLoop54}
				params={{ q, facetCategory }}
				buildChildren={(data) => (
					<>
						{data.results.items.map((p) => (
							<ProductBox
								key={p.id}
								product={p}
								onPurchaseCallback={sendSuccessfulSearchEvent}
								renderedInSearchResults
							/>
						))}
					</>
				)}
				postPayload={loop54SearchPayload}
				postHeaders={loop54PostHeaders}
			/>
		</Main>
	);
};

type QueryDescriptionProps = { query: string; total: number; facet?: string };
export const QueryDescription = ({ query, total, facet }: QueryDescriptionProps): JSX.Element => {
	const t = useTranslation().search;
	const urls = useTranslation().routes;
	const querySentence = renderTemplate(t.result, { query, total });
	if (!facet) return <>{querySentence}</>;
	const facetTag = <SearchFacet text={facet} removeUrl={`${urls.search}?${toQuery({ q: query })}`} />;
	return <Interpolated template={t.resultWithinFacet} data={{ query, facet: facetTag, total }} />;
};

export const NoSearchQuery = (): JSX.Element => {
	const t = useTranslation().search;
	return <p>{t.noQueryProvided}</p>;
};

type SuggestionProps = { suggestion: string };
export const Suggestion = ({ suggestion }: SuggestionProps): JSX.Element => {
	const t = useTranslation().search;
	const urls = useTranslation().routes;
	return (
		<p>
			{t.didYouMean} <Link to={`${urls.search}?${toQuery({ q: suggestion })}`}>{suggestion}</Link>?
		</p>
	);
};

export const NoResults = (): JSX.Element => {
	const t = useTranslation().search;
	return (
		<ContentCard>
			<h2>{t.tip}</h2>
			<ul>
				<li>{t.fewerWords}</li>
				<li>{t.checkSpelling}</li>
				<li>{t.moreGenericQuery}</li>
				<li>{t.splitWords}</li>
			</ul>
		</ContentCard>
	);
};
